// Dark Mode Copy-Paste Formatting (#345).
//
// The browser's DEFAULT copy handler serializes the COMPUTED style of the
// selection into the clipboard's `text/html` flavor — dark bubble background,
// light text color, the PAI design-system's link/underline treatment — so
// pasting a PAI reply into Gmail/Word/Outlook carried a dark box, forced text
// color and a blue-underlined link style regardless of theme. That happens
// entirely inside the browser's native clipboard writer; it is NOT caused by
// any inline `style=` attribute this app sets (there are none on message
// content — colors come from Tailwind classes/CSS, which the native writer
// resolves into computed styles at copy time).
//
// The only way to stop it is to intercept the `copy` event, build our OWN
// `text/html` (and `text/plain`) payload from scratch, and call
// `event.preventDefault()` so the native writer never runs. This module is
// the pure, DOM-based sanitizer that does that: it walks the user's selected
// DOM range and produces a semantic-only fragment — paragraphs, lists,
// tables, bold/italic, links (href only), headings, code — with EVERY
// style/class/data/aria attribute stripped, so the destination app (Gmail,
// Word, Outlook, a plain textarea) renders it with its OWN default styling.
// Because no inline style is ever written, dark mode and light mode produce
// byte-identical output — there is no per-theme branch anywhere here.
//
// Kept deliberately separate from any live `Selection`/`Range` so it can be
// unit-tested with plain DOM nodes built in jsdom.

// Tags that survive as themselves, with every attribute removed except
// `href` on `<a>`. This is a MEANING allowlist, not a styling one — nothing
// here carries color/background/underline decisions of its own, so a
// destination app is always free to render it with its native defaults.
const ALLOWED_TAGS = new Set([
    'p', 'br', 'hr',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
    'b', 'strong', 'i', 'em', 'a', 'del', 's',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote', 'code', 'pre',
]);

// Tags that are pure UI chrome or cannot mean anything to a paste target —
// dropped ENTIRELY, subtree included. `svg`/`path`/`button` covers every
// icon + action button rendered inside a message (reply/edit, code-block
// "Copy", …); none of their glyphs or button labels belong in copied text.
const DROP_TAGS = new Set([
    'script', 'style', 'button', 'svg', 'path', 'img',
    'input', 'textarea', 'select', 'option', 'canvas',
    'iframe', 'object', 'embed', 'video', 'audio', 'noscript',
]);

// Anything not in either set above (div, span, mark, section, …) is
// UNWRAPPED: the wrapper itself carries no meaning worth preserving (and is
// exactly where a background-color / search-highlight / theme class would
// have lived), but its text content is real message content and must
// survive — so its children are kept, re-parented onto whatever ancestor
// tag is being built.

function shouldDrop(el) {
    if (DROP_TAGS.has(el.tagName.toLowerCase())) return true;
    if (el.getAttribute && el.getAttribute('aria-hidden') === 'true') return true;
    // CodeBlock's language/"Copy" header bar — UI chrome, never content.
    if (el.hasAttribute && el.hasAttribute('data-clipboard-skip')) return true;
    return false;
}

/**
 * Recursively rebuild `node` using only ALLOWED_TAGS, dropping DROP_TAGS
 * subtrees, and unwrapping everything else into its own children.
 * Returns a Node (Element or DocumentFragment) to append to the caller's
 * container, or `null` if the node contributed nothing.
 */
function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
        return document.createTextNode(node.nodeValue);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        // Comments and anything else carry no content worth copying.
        return null;
    }
    if (shouldDrop(node)) return null;

    const tag = node.tagName.toLowerCase();
    const builtChildren = document.createDocumentFragment();
    node.childNodes.forEach((child) => {
        const built = walk(child);
        if (built) builtChildren.appendChild(built);
    });

    if (ALLOWED_TAGS.has(tag)) {
        const el = document.createElement(tag);
        if (tag === 'a') {
            const href = node.getAttribute && node.getAttribute('href');
            if (href) el.setAttribute('href', href);
        }
        el.appendChild(builtChildren);
        return el;
    }
    // Unwrap: hand the processed children straight to the caller.
    return builtChildren;
}

// Elements after which plain-text extraction inserts a line break — the
// same set of tags that read as a "block" in the sanitized (already
// allowlisted) output.
const BLOCK_TAGS = new Set([
    'p', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'table', 'hr',
]);

function extractPlainText(container) {
    let text = '';
    const visit = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.nodeValue;
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const tag = node.tagName.toLowerCase();
        if (tag === 'br') {
            text += '\n';
            return;
        }
        node.childNodes.forEach(visit);
        if (tag === 'td' || tag === 'th') text += '\t';
        if (BLOCK_TAGS.has(tag)) text += '\n';
    };
    container.childNodes.forEach(visit);
    return text
        .replace(/[ \t]+\n/g, '\n') // trailing cell tabs before a row break
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * Sanitize an arbitrary DOM subtree (typically `Range.cloneContents()`, a
 * `DocumentFragment`) into a `{ html, text }` clipboard payload. Both
 * outputs are theme-agnostic and carry no style/class/data attributes —
 * `html` a semantic-only fragment, `text` its visible text with block-level
 * line breaks.
 */
export function sanitizeNodeForClipboard(root) {
    const container = document.createElement('div');
    if (root.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        root.childNodes.forEach((child) => {
            const built = walk(child);
            if (built) container.appendChild(built);
        });
    } else {
        const built = walk(root);
        if (built) container.appendChild(built);
    }
    return {
        html: container.innerHTML,
        text: extractPlainText(container),
    };
}

/**
 * Convenience wrapper over a live `Selection` (defaults to
 * `window.getSelection()`). Returns `null` when there is nothing selected
 * (collapsed / no ranges) so the caller can let the native copy proceed
 * untouched.
 */
export function sanitizeSelectionForClipboard(selection) {
    const sel = selection || (typeof window !== 'undefined' ? window.getSelection() : null);
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    const fragment = range.cloneContents();
    const payload = sanitizeNodeForClipboard(fragment);
    if (!payload.html && !payload.text) return null;
    return payload;
}
